import { useState } from "react"
import styled from "styled-components"

import { Button, colors, Text } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { SearchFilter } from "src/components/Filter/SearchFilter"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { ParadiseAppliedFilters } from "src/components/Paradise/ParadiseAppliedFilters"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import {
  Ellipsis,
  IdWrapper,
  ParadiseLayout,
  ParadiseTitle,
  PillsWrapper,
  TopWrapper,
} from "src/components/Paradise/sharedStyles"
import { useFetchParadiseUsers } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { isSuperAdmin } from "src/data/user/logic/accessLogic"
import { useParadiseFavorites } from "src/hooks/useParadiseFavorites"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { useSearchParams } from "src/router/useSearchParams"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import InfoIcon from "src/ui/icons/info.svg"
import StarIcon from "src/ui/icons/star.svg"
import StarIconOutline from "src/ui/icons/star-outline.svg"
import { Titlebar } from "src/ui/Layout/Titlebar"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { spacing } from "src/ui/spacing"
import { formatUtcDate } from "src/utils/l10n"

import { ParadiseCreateUserDialog } from "./ParadiseCreateUserDialog"
type TParadiseUsersSearchFilter = {
  user_id: string | null
  name: string | null
  email: string | null
}

const usersFilterLabel: Record<keyof TParadiseUsersSearchFilter, string> = {
  user_id: "User ID",
  email: "Email",
  name: "Name",
} as const

const LIMIT = 50

const StarWrapper = styled.div`
  .star-outline {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover .star-outline {
    opacity: 1;
  }
`

export function ParadiseUsers() {
  const { navigate } = useRouter()
  const user = useGetUser()

  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false)

  const { limit, offset, setOffset } = useUrlPager({ initialLimit: LIMIT })

  const {
    searchParams: filter,
    setSearchParams: setFilter,
    sortedSearchParams,
  } = useSearchParams({
    keys: [
      {
        key: "user_id",
        type: "string",
      },
      {
        key: "name",
        type: "string",
      },
      {
        key: "email",
        type: "string",
      },
    ],
  })

  const { favorites: favoriteIds, toggleFavorite } =
    useParadiseFavorites("user")

  const favoriteUsers = useFetchParadiseUsers({
    filter: {
      ids: favoriteIds.join(","),
    },
    options: {
      enabled: favoriteIds.length > 0,
    },
  })

  const tableColumns: TableColumn<TParadiseUser>[] = [
    {
      label: "",
      value: "favorite",
      columnWidth: "min-content",
      hidden: favoriteIds.length === 0,
      disableClickPropagation: true,
      renderCell: (data) => (
        <StarWrapper
          onClick={(e) => {
            e.stopPropagation()
            toggleFavorite(data.id)
          }}
        >
          {favoriteIds.includes(data.id) ? (
            <StarIcon fill={colors.systemWarning} width={16} />
          ) : (
            <div className="star-outline">
              <StarIconOutline width={16} />
            </div>
          )}
        </StarWrapper>
      ),
    },
    {
      label: "User id",
      value: "user_id",
      disabled: true,
      columnWidth: "min-content",
      disableClickPropagation: true,
      renderCell: (data) => (
        <div>
          <IdWrapper>
            <InternalLink to={Routes.ParadiseUser.location(data.id).pathname}>
              {data.id}
            </InternalLink>
            {data.deleted && (
              <MTooltip title="Deleted">
                <InfoIcon width={12} fill={colors.systemError} />
              </MTooltip>
            )}
          </IdWrapper>
        </div>
      ),
      popoverContent: (
        <ColumnPopoverWrapper>
          <SearchFilter
            initialValue={filter.user_id ?? undefined}
            placeholder="Search by id"
            onChange={(v) => setFilter("user_id", v)}
          />
          <Text variant="bodyS">Make sure to type in the exact ID</Text>
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      label: "Email",
      value: "email",
      columnWidth: 300,
      renderCell: (data) => <Ellipsis>{data.email}</Ellipsis>,
      popoverContent: (
        <ColumnPopoverWrapper>
          <SearchFilter
            initialValue={filter.email ?? undefined}
            onChange={(v) => handleFilterChange("email", v)}
            placeholder="Search by email"
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      label: "Name",
      value: "name",
      disabled: true,
      columnWidth: "min-content",
      renderCell: (data) => <Ellipsis>{data.full_name}</Ellipsis>,
      popoverContent: (
        <ColumnPopoverWrapper>
          <SearchFilter
            initialValue={filter.name ?? undefined}
            onChange={(v) => handleFilterChange("name", v)}
            placeholder="Search by email"
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "created_at",
      label: "Created at",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => (
        <div>{formatUtcDate({ date: data.created_at, excludeTime: true })}</div>
      ),
    },
    {
      value: "last_seen_at",
      label: "Last seen at",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => (
        <div>
          {formatUtcDate({ date: data.created_at, excludeTime: false })}
        </div>
      ),
    },
    {
      value: "subscription_plan_id",
      label: "Subscription plan id",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => <div>{data.subscription_plan_id}</div>,
    },
    {
      value: "subscription_status",
      label: "Subscription status",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => (
        <div>
          <div>
            <Badge
              size="x-small"
              color={
                data.subscription_status === "active"
                  ? "good"
                  : data.subscription_status === "cancelled"
                    ? "error"
                    : "neutral"
              }
            >
              {data.subscription_status}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      value: "email_verification",
      label: "Email verified",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => (
        <div>
          <div>
            <Badge
              size="x-small"
              color={data.email_verification?.verified ? "good" : "neutral"}
            >
              {data.email_verification?.verified ? "Yes" : "No"}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      label: "Roles",
      value: "roles",
      columnWidth: "auto",
      renderCell: (data) => (
        <div>
          <PillsWrapper $noWrap>
            {data.roles.map((role, i) => (
              <Badge key={i} size="x-small">
                {role}
              </Badge>
            ))}
          </PillsWrapper>
        </div>
      ),
    },
    {
      value: "deleted",
      label: "Deleted",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (data) => <div>{data.deleted ? "Yes" : "No"}</div>,
    },
  ]

  const fetchedUsers = useFetchParadiseUsers({
    filter: {
      limit,
      offset,
      user_id: filter.user_id || undefined,
      name: filter.name || undefined,
      email: filter.email || undefined,
      sort: "name",
      sort_by: "asc",
    },
    options: {
      keepPreviousData: true,
    },
  })

  let data: TParadiseUser[] = []
  const usersWithoutFavorites =
    fetchedUsers.data?.users.filter((user) => !favoriteIds.includes(user.id)) ??
    []
  // Only add the favorites at the start if you're on the
  // first page and not filtering by anything
  if (
    favoriteUsers.data?.users.length &&
    favoriteUsers.data?.users.length > 0 &&
    offset === 0 &&
    sortedSearchParams.length === 0
  ) {
    data = [...favoriteUsers.data.users, ...usersWithoutFavorites]
  } else {
    data = fetchedUsers.data?.users ?? []
  }

  const {
    headerElements,
    interactiveColumns,
    interactiveVisibleColumns,
    rows,
    updateColumnVisibility,
    templateColumns,
  } = useTableColumns({
    columns: tableColumns,
    data: data,
    options: { localStorageKey: "minut.paradise.users.table" },
  })

  function handleFilterChange(filterKey: keyof typeof filter, value: string) {
    setOffset(0)
    setFilter(filterKey, value)
  }

  return (
    <ParadiseLayout $noLeftPadding={favoriteIds.length > 0}>
      <Titlebar
        bottomMargin={spacing.S}
        leftMargin={favoriteIds.length > 0 ? spacing.L : 0}
        title={
          <ParadiseTitle>
            Users
            {fetchedUsers.isSuccess && (
              <Badge color="info">
                Total: {fetchedUsers.data?.total_count}
              </Badge>
            )}
          </ParadiseTitle>
        }
        actionBar={
          <Button size="small" onClick={() => setShowCreateUserDialog(true)}>
            Create user
          </Button>
        }
        size="unset"
      />

      <TopWrapper>
        <div>
          <ParadiseAppliedFilters
            filters={sortedSearchParams.map((f) => ({
              label: usersFilterLabel[f.key],
              value: f.value.toString(),
              onRemove: () => setFilter(f.key, null),
            }))}
          />
        </div>

        <DropdownMultiSelect
          label="Columns"
          selectedValues={interactiveVisibleColumns.map((c) => c.value)}
          options={interactiveColumns}
          onChange={({ checked, option }) => {
            updateColumnVisibility(option.value, !checked)
          }}
        />
      </TopWrapper>

      <ParadiseTable
        rows={rows ?? []}
        templateColumns={templateColumns}
        header={headerElements}
        onRowClick={(index) => {
          if (!isSuperAdmin(user.roles) || !data[index]) {
            return
          }
          navigate(Routes.ParadiseUser.location(data[index].user_id))
        }}
        error={{
          hasError: fetchedUsers.isError,
          title: fetchedUsers.error?.message,
        }}
      />
      <ParadisePager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchedUsers.data?.total_count}
      />

      <ParadiseCreateUserDialog
        open={showCreateUserDialog}
        onClose={() => setShowCreateUserDialog(false)}
      />
    </ParadiseLayout>
  )
}
